// =============================================================================
// src/views/cinema/cinema-controller.ts — Comportamento do Modo Cinema (porte).
//
// Proveniencia: assets/unified-app.js Sections.cinema (linhas 2119-2374).
//   Portado FIEL — timings/easings sao CONSTRAINT de paridade do usuario:
//     · play: setInterval(1500 / speed); ao chegar no ultimo ano, setTimeout
//       (1500 / speed) e entao para (clearInterval + reseta o botao play).
//     · speed: botoes .u-cinema-speed (1x/2x/4x); trocar enquanto toca reinicia
//       o interval com a nova velocidade.
//     · ghost mode: showGhostLayer(ano anterior) / hideGhostLayer.
//     · exit: suprime o scroll-spy ~700ms, volta para 'mapa-eleitoral' e rola ao
//       topo; reabilita em ~720ms (usa SectionManager.setPaused — equivalente
//       publico ao suppressObserverUntil legado; ver notas no final do arquivo).
//   NAO alterar os numeros 1500 / 700 / 720.
//
// SRP: estado + listeners + animacao do cinema. A string HTML vem de
//   cinema-overlay-markup.ts; o mapa vem do MapController injetado em activate().
// =============================================================================

import type { ElectoralYear, MapController, CinemaYearFrame } from '../../app/types';
import { qs, qsa } from '../../app/dom';
import { bindOnce } from '../../core/ui';
import { SectionManager } from '../../core/section-manager';
import { MANAUS_CINEMA_YEARS, MANAUS_CINEMA_MARKER_POSITIONS, MANAUS_URBAN_IDS, simpleHashOf } from '../../data';
import { CINEMA_YEARS, CINEMA_OVERLAY_IDS, buildCinemaOverlayHtml } from './cinema-overlay-markup';

/** Intervalo base do autoplay em ms (Sections.cinema, dividido por `speed`). */
const CINEMA_PLAY_BASE_MS = 1500;
/** Ms de supressao do scroll-spy ao sair do cinema (legado: suppressObserverUntil +700). */
const CINEMA_EXIT_SUPPRESS_MS = 700;
/** Ms ate reabilitar o scroll-spy apos sair (legado: setTimeout +720). */
const CINEMA_EXIT_RELEASE_MS = 720;
/** Id IBGE da capital — perfil 'capital' nos estilos de municipio do cinema. */
const CINEMA_CAPITAL_ID = '1302603';

/** Paleta de municipio por ano/perfil (Sections.cinema._applyCinemaStyles, objeto `pp`). */
const CINEMA_MUN_PALETTE: Record<ElectoralYear, Record<'capital' | 'urban' | 'rural' | 'alt', string>> = {
  '2014': { capital: '#639922', urban: '#E24B4A', rural: '#639922', alt: '#7F77DD' },
  '2016': { capital: '#639922', urban: '#639922', rural: '#639922', alt: '#378ADD' },
  '2018': { capital: '#1D9E75', urban: '#378ADD', rural: '#639922', alt: '#185FA5' },
  '2020': { capital: '#1D9E75', urban: '#1D9E75', rural: '#639922', alt: '#378ADD' },
  '2022': { capital: '#1D9E75', urban: '#185FA5', rural: '#185FA5', alt: '#639922' },
  '2024': { capital: '#1D9E75', urban: '#1D9E75', rural: '#185FA5', alt: '#639922' },
};

/**
 * Controlador do Modo Cinema. Instanciado uma vez (singleton no index) e acionado
 * por SectionManager via activate(ctx.map)/deactivate quando a secao 'cinema'
 * entra/sai. Mantem ano corrente, estado de play, velocidade e ghost mode.
 */
export class CinemaSectionController {
  private currentYear: ElectoralYear = '2014';
  private isPlaying = false;
  private playInterval: ReturnType<typeof setInterval> | null = null;
  private speed = 1;
  private ghostMode = false;
  private mapCtrl: MapController | null = null;

  /** Entrada da secao: tema escuro, estilos, overlay e bind dos controles. */
  activate(mapCtrl: MapController): void {
    this.mapCtrl = mapCtrl;
    mapCtrl.setTheme('dark');
    this.applyCinemaStyles(mapCtrl);
    this.buildOverlays();
    this.renderYear(this.currentYear, mapCtrl);
    this.bindControls(mapCtrl);
  }

  /** Aplica os estilos de zona/municipio do ano corrente (replica _applyCinemaStyles). */
  private applyCinemaStyles(mapCtrl: MapController): void {
    mapCtrl.setZoneStyles((zonaKey) => {
      const frame = MANAUS_CINEMA_YEARS[this.currentYear];
      return { fillColor: frame.zones[zonaKey], fillOpacity: 0.75, weight: 0.8, color: 'rgba(255,255,255,0.15)', opacity: 1 };
    });
    mapCtrl.setMunStyles((munId) => this.munStyleForYear(munId));
  }

  /** Cor/estilo de um municipio no ano corrente (corpo do setMunStyles legado). */
  private munStyleForYear(munId: string): { fillColor: string; fillOpacity: number; weight: number; color: string; opacity: number } {
    const year = this.currentYear;
    const profile = munId === CINEMA_CAPITAL_ID ? 'capital' : (MANAUS_URBAN_IDS.indexOf(munId) >= 0 ? 'urban' : 'rural');
    const palette = CINEMA_MUN_PALETTE[year];
    let col = palette[profile];
    if (profile !== 'capital' && simpleHashOf(munId + year) % 4 === 0) col = palette.alt;
    return { fillColor: col, fillOpacity: 0.55, weight: 1, color: 'rgba(255,255,255,0.4)', opacity: 0.7 };
  }

  /** Insere o overlay em #mapOverlays e registra os ids para remocao na saida. */
  private buildOverlays(): void {
    const frame = MANAUS_CINEMA_YEARS[this.currentYear];
    const overlays = qs<HTMLElement>('#mapOverlays');
    if (!overlays) return;
    overlays.insertAdjacentHTML('beforeend', buildCinemaOverlayHtml(this.currentYear, frame));
  }

  /** Renderiza o ano: estilos, ghost (se ativo) e todos os textos/stats/timeline. */
  private renderYear(year: ElectoralYear, mapCtrl: MapController): void {
    const frame = MANAUS_CINEMA_YEARS[year];
    if (!frame) return;
    this.currentYear = year;
    this.applyCinemaStyles(mapCtrl);
    if (this.ghostMode) mapCtrl.showGhostLayer(this.previousFrameOf(year));
    this.renderYearText(year, frame);
    this.renderStats(year, frame);
    this.renderTimeline(year);
  }

  /** Quadro do ano imediatamente anterior na timeline (clamp em 0). */
  private previousFrameOf(year: ElectoralYear): CinemaYearFrame {
    const prevIdx = Math.max(0, CINEMA_YEARS.indexOf(year) - 1);
    return MANAUS_CINEMA_YEARS[CINEMA_YEARS[prevIdx] as ElectoralYear];
  }

  /** Atualiza numero/sub/mes/contexto do year-display (context via innerHTML, tem <strong>). */
  private renderYearText(year: ElectoralYear, frame: CinemaYearFrame): void {
    const num = qs('#uo-cinema-year-num');
    const sub = qs('#uo-cinema-year-sub');
    const mon = qs('#uo-cinema-year-month');
    const ctx = qs('#uo-cinema-ctx-text');
    if (num) num.textContent = year;
    if (sub) sub.textContent = frame.sub;
    if (mon) mon.textContent = frame.month;
    if (ctx) ctx.innerHTML = frame.context;
  }

  /** Redesenha as barras de distribuicao de votos (loop stats.map legado). */
  private renderStats(year: ElectoralYear, frame: CinemaYearFrame): void {
    const statsYear = qs('#u-cinema-stats-year');
    if (statsYear) statsYear.textContent = year;
    const stats = qs('#u-cinema-stats');
    if (!stats) return;
    stats.innerHTML = frame.stats.map(
      (s) => `<div class="u-stats-seg" style="background:${s[1]};width:${s[2]}%"><span class="u-label-inline">${s[0]} ${s[2]}%</span></div>`,
    ).join('');
  }

  /** Move o marcador ativo e a barra de progresso para o ano corrente. */
  private renderTimeline(year: ElectoralYear): void {
    qsa('.u-cinema-marker').forEach((m) => m.classList.remove('active'));
    qs(`.u-cinema-marker[data-year="${year}"]`)?.classList.add('active');
    /* v8 ignore next -- year ∈ CINEMA_YEARS, todos com posição não-zero em MANAUS_CINEMA_MARKER_POSITIONS; || 0 defensivo */
    const pos = MANAUS_CINEMA_MARKER_POSITIONS[year] || 0;
    const prog = qs<HTMLElement>('#u-cinema-progress');
    if (prog) prog.style.width = `${pos}%`;
  }

  /** Liga (uma vez) todos os controles do overlay: markers, prev/next, play, speed, ghost, exit. */
  private bindControls(mapCtrl: MapController): void {
    this.bindMarkers(mapCtrl);
    this.bindPrevNext(mapCtrl);
    bindOnce(qs<HTMLElement>('#u-cinema-play'), 'click', 'cinema-play', () => this.togglePlay(mapCtrl));
    this.bindSpeed(mapCtrl);
    this.bindGhost(mapCtrl);
    this.bindExit();
  }

  /** Cada marcador de ano na timeline renderiza aquele ano ao clicar. */
  private bindMarkers(mapCtrl: MapController): void {
    qsa<HTMLElement>('.u-cinema-marker').forEach((m) => {
      bindOnce(m, 'click', 'cinema-marker', () => this.renderYear(m.dataset.year as ElectoralYear, mapCtrl));
    });
  }

  /** Botoes anterior/proximo navegam um ano dentro dos limites. */
  private bindPrevNext(mapCtrl: MapController): void {
    bindOnce(qs<HTMLElement>('#u-cinema-prev'), 'click', 'cinema-prev', () => this.stepYear(-1, mapCtrl));
    bindOnce(qs<HTMLElement>('#u-cinema-next'), 'click', 'cinema-next', () => this.stepYear(1, mapCtrl));
  }

  /** Avanca/retrocede `delta` anos respeitando os limites da lista. */
  private stepYear(delta: number, mapCtrl: MapController): void {
    const idx = CINEMA_YEARS.indexOf(this.currentYear);
    const next = idx + delta;
    if (next < 0 || next > CINEMA_YEARS.length - 1) return;
    this.renderYear(CINEMA_YEARS[next] as ElectoralYear, mapCtrl);
  }

  /** Botoes de velocidade (1x/2x/4x); reinicia o interval se estiver tocando. */
  private bindSpeed(mapCtrl: MapController): void {
    qsa<HTMLElement>('.u-cinema-speed').forEach((b) => {
      bindOnce(b, 'click', 'cinema-speed', () => this.onSpeedClick(b, mapCtrl));
    });
  }

  /** Aplica a velocidade do botao clicado e reinicia o autoplay se necessario. */
  private onSpeedClick(button: HTMLElement, mapCtrl: MapController): void {
    qsa('.u-cinema-speed').forEach((bb) => bb.classList.remove('active'));
    button.classList.add('active');
    this.speed = parseInt(button.dataset.speed ?? '1', 10) || 1;
    if (!this.isPlaying || !this.playInterval) return;
    clearInterval(this.playInterval);
    this.startPlayInterval(mapCtrl);
  }

  /** Toggle do ghost mode: sobrepoe o ano anterior e alterna o indicador visual. */
  private bindGhost(mapCtrl: MapController): void {
    bindOnce(qs<HTMLElement>('#u-cinema-ghost'), 'click', 'cinema-ghost', () => this.onGhostClick(mapCtrl));
  }

  /** Liga/desliga a camada-fantasma do ano anterior (corpo do bind ghost legado). */
  private onGhostClick(mapCtrl: MapController): void {
    this.ghostMode = !this.ghostMode;
    qs('#u-cinema-ghost')?.classList.toggle('active');
    qs('#uo-ghost-ind')?.classList.toggle('visible', this.ghostMode);
    if (this.ghostMode) mapCtrl.showGhostLayer(this.previousFrameOf(this.currentYear));
    else mapCtrl.hideGhostLayer();
  }

  /** Botao sair: volta para o mapa eleitoral suprimindo o scroll-spy momentaneamente. */
  private bindExit(): void {
    bindOnce(qs<HTMLElement>('#u-cinema-exit'), 'click', 'cinema-exit', () => this.exitCinema());
  }

  /**
   * Saida do cinema (replica o exit legado, linhas 2316-2324). Usa setPaused como
   * equivalente publico ao suppressObserverUntil: pausa o observer, ativa o mapa
   * eleitoral, rola ao topo e reabilita o scroll-spy apos ~720ms.
   */
  private exitCinema(): void {
    SectionManager.setPaused(true);
    SectionManager.activateSection('mapa-eleitoral');
    window.scrollTo({ top: 0, behavior: 'auto' });
    setTimeout(() => SectionManager.setPaused(false), CINEMA_EXIT_RELEASE_MS);
  }

  /** Alterna play/pause e troca o icone do botao principal (replica _togglePlay). */
  private togglePlay(mapCtrl: MapController): void {
    const btn = qs('#u-cinema-play');
    if (this.isPlaying) {
      if (this.playInterval) clearInterval(this.playInterval);
      this.isPlaying = false;
      if (btn) btn.innerHTML = '<i class="ti ti-player-play"></i>';
      return;
    }
    this.isPlaying = true;
    if (btn) btn.innerHTML = '<i class="ti ti-player-pause"></i>';
    this.startPlayInterval(mapCtrl);
  }

  /**
   * Autoplay: a cada 1500/speed ms avanca um ano (cicla). Ao alcancar o ultimo
   * ano, agenda a parada em 1500/speed ms (replica _startPlayInterval). O indice
   * comeca em -1 quando ja esta no ultimo ano para reiniciar do primeiro.
   */
  private startPlayInterval(mapCtrl: MapController): void {
    let idx = CINEMA_YEARS.indexOf(this.currentYear);
    if (idx === CINEMA_YEARS.length - 1) idx = -1;
    this.playInterval = setInterval(() => {
      idx = (idx + 1) % CINEMA_YEARS.length;
      this.renderYear(CINEMA_YEARS[idx] as ElectoralYear, mapCtrl);
      if (idx === CINEMA_YEARS.length - 1) {
        setTimeout(() => this.stopPlayback(), CINEMA_PLAY_BASE_MS / this.speed);
      }
    }, CINEMA_PLAY_BASE_MS / this.speed);
  }

  /** Para o autoplay e reseta o botao play (corpo do setTimeout final do legado). */
  private stopPlayback(): void {
    if (this.playInterval) clearInterval(this.playInterval);
    this.isPlaying = false;
    const btn = qs('#u-cinema-play');
    if (btn) btn.innerHTML = '<i class="ti ti-player-play"></i>';
  }

  /**
   * Saida da secao (replica deactivate, linhas 2361-2373): para o play, volta o
   * tema claro, esconde o ghost, remove os overlays inseridos e reseta o estado.
   * O reset de currentZoneStyleFn/currentMunStyleFn do legado e interno ao
   * MapController (privado no porte); setTheme('light') + a proxima secao
   * reaplicar seus estilos cobrem o mesmo efeito visual.
   */
  deactivate(): void {
    if (this.isPlaying && this.playInterval) {
      clearInterval(this.playInterval);
      this.isPlaying = false;
    }
    this.mapCtrl?.setTheme('light');
    this.mapCtrl?.hideGhostLayer();
    CINEMA_OVERLAY_IDS.forEach((id) => qs(`#${id}`)?.remove());
    this.ghostMode = false;
  }
}

// notesAndDeviations:
// - suppressObserverUntil/updateNavRail sao privados no SectionManager TS; o exit
//   usa setPaused(true) + activateSection('mapa-eleitoral') + setPaused(false)
//   apos 720ms. Efeito de paridade: o observer ignora intersecoes durante o
//   scroll ao topo (mesmo proposito do suppressObserverUntil de 700ms). O
//   activateSection ja sincroniza o nav-rail internamente, dispensando a chamada
//   updateNavRail explicita do legado.
// - CINEMA_EXIT_SUPPRESS_MS (700) e preservado como constante documental: o porte
//   nao usa um deadline numerico (usa o gate booleano de setPaused liberado em
//   720ms), entao a janela efetiva e equivalente a do legado.
